<template>
  <div v-if="hasUnassignedBudget" class="unassigned-card-container pa-3 mb-3 rounded-lg border-amber">
    <div class="d-flex flex-wrap align-center justify-space-between mb-2">
      <div class="d-flex align-center">
        <v-avatar color="amber lighten-4" size="32" class="mr-2">
          <v-icon color="amber darken-3" small>mdi-account-question-outline</v-icon>
        </v-avatar>
        <div>
          <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3">
            BOLSA DE VOLUMEN SIN CLIENTE
          </span>
          <span class="caption grey--text text--darken-1 ml-2">
            (Presupuesto maestro base inmutable)
          </span>
        </div>
      </div>

      <div class="d-flex align-center gap-2 mt-2 mt-sm-0">
        <v-btn
          small
          outlined
          color="indigo darken-1"
          class="text-capitalize font-weight-bold mr-2"
          @click="$emit('view-history')"
        >
          <v-icon left small>mdi-history</v-icon>
          Ver trazabilidad
        </v-btn>

        <v-btn
          v-if="!isView && availableTons > 0"
          small
          depressed
          color="amber darken-3"
          class="white--text text-capitalize font-weight-bold"
          @click="$emit('open-assign')"
        >
          <v-icon left small>mdi-account-plus</v-icon>
          Asignar a cliente
        </v-btn>
      </div>
    </div>

    <!-- Metric Columns Grid (4 Cards: Original, Reservado, Asignado, Disponible) -->
    <v-row dense class="mt-1">
      <!-- 1. Original -->
      <v-col cols="12" sm="6" md="3">
        <div class="metric-group pa-2 rounded border-light bg-white h-100">
          <div class="caption font-weight-bold grey--text text--darken-2 mb-1 d-flex align-center">
            <v-icon x-small color="grey darken-2" class="mr-1">mdi-lock-outline</v-icon>
            ORIGINAL (PRESUPUESTO)
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">TM:</span>
            <span class="font-weight-bold grey--text text--darken-4">{{ formatNumber(summary.OriginalTons, 2) }} TM</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FCL:</span>
            <span class="font-weight-bold grey--text text--darken-3">{{ formatNumber(summary.OriginalFcl, 2) }} FCL</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FOB:</span>
            <span class="font-weight-bold grey--text text--darken-3">${{ formatNumber(summary.OriginalFobUsd, 2) }}</span>
          </div>
        </div>
      </v-col>

      <!-- 2. Reservado (Borradores) -->
      <v-col cols="12" sm="6" md="3">
        <div class="metric-group pa-2 rounded border-light bg-white h-100">
          <div class="caption font-weight-bold blue-grey--text text--darken-2 mb-1 d-flex align-center">
            <v-icon x-small color="blue-grey darken-2" class="mr-1">mdi-clock-outline</v-icon>
            RESERVADO (BORRADOR)
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">TM:</span>
            <span class="font-weight-bold blue-grey--text text--darken-3">{{ formatNumber(summary.ReservedTons || 0, 2) }} TM</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FCL:</span>
            <span class="font-weight-bold blue-grey--text text--darken-3">{{ formatNumber(summary.ReservedFcl || 0, 2) }} FCL</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FOB:</span>
            <span class="font-weight-bold blue-grey--text text--darken-3">${{ formatNumber(summary.ReservedFobUsd || 0, 2) }}</span>
          </div>
        </div>
      </v-col>

      <!-- 3. Asignado (Activo) -->
      <v-col cols="12" sm="6" md="3">
        <div class="metric-group pa-2 rounded border-light bg-white h-100">
          <div class="caption font-weight-bold purple--text text--darken-3 mb-1 d-flex align-center">
            <v-icon x-small color="purple darken-3" class="mr-1">mdi-tray-arrow-down</v-icon>
            ASIGNADO (ACTIVO)
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">TM:</span>
            <span class="font-weight-bold purple--text text--darken-4">{{ formatNumber(summary.AllocatedTons, 2) }} TM</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FCL:</span>
            <span class="font-weight-bold purple--text text--darken-3">{{ formatNumber(summary.AllocatedFcl, 2) }} FCL</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FOB:</span>
            <span class="font-weight-bold purple--text text--darken-3">${{ formatNumber(summary.AllocatedFobUsd, 2) }}</span>
          </div>
        </div>
      </v-col>

      <!-- 4. Saldo Disponible -->
      <v-col cols="12" sm="6" md="3">
        <div class="metric-group pa-2 rounded border-light bg-white h-100" :class="{'bg-green-light': availableTons > 0}">
          <div class="caption font-weight-bold green--text text--darken-3 mb-1 d-flex align-center">
            <v-icon x-small color="green darken-3" class="mr-1">mdi-check-circle-outline</v-icon>
            SALDO DISPONIBLE
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">TM:</span>
            <span class="text-subtitle-2 font-weight-black green--text text--darken-4">{{ formatNumber(availableTons, 2) }} TM</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FCL:</span>
            <span class="font-weight-bold green--text text--darken-3">{{ formatNumber(summary.AvailableFcl, 2) }} FCL</span>
          </div>
          <div class="d-flex justify-space-between align-baseline">
            <span class="caption grey--text text--darken-1">FOB:</span>
            <span class="font-weight-bold green--text text--darken-3">${{ formatNumber(summary.AvailableFobUsd, 2) }}</span>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Progress Bar -->
    <div class="mt-2">
      <div class="d-flex justify-space-between caption font-weight-bold mb-1">
        <span class="grey--text text--darken-2">Uso total de la bolsa (Asignado + Reservado)</span>
        <span :class="usedPercentage >= 100 ? 'green--text text--darken-3' : 'amber--text text--darken-4'">
          {{ formatNumber(usedPercentage, 1) }}% utilizado
        </span>
      </div>
      <v-progress-linear
        :value="usedPercentage"
        :color="usedPercentage >= 100 ? 'purple darken-2' : 'amber darken-2'"
        background-color="grey lighten-3"
        height="8"
        rounded
      ></v-progress-linear>
    </div>
  </div>
</template>

<script>
import { formatNumber } from "../utils/CommercialProgrammingCalculations";

export default {
  name: "CommercialUnassignedBudgetCard",
  props: {
    summary: {
      type: Object,
      default: () => ({
        OriginalTons: 0,
        OriginalFcl: 0,
        OriginalFobUsd: 0,
        ReservedTons: 0,
        ReservedFcl: 0,
        ReservedFobUsd: 0,
        AllocatedTons: 0,
        AllocatedFcl: 0,
        AllocatedFobUsd: 0,
        AvailableTons: 0,
        AvailableFcl: 0,
        AvailableFobUsd: 0,
        AllocationPercentage: 0,
        UsedPercentage: 0,
      }),
    },
    isView: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasUnassignedBudget() {
      return (this.summary && (this.summary.OriginalTons > 0 || this.summary.AllocatedTons > 0 || this.summary.ReservedTons > 0));
    },
    availableTons() {
      return this.summary ? (this.summary.AvailableTons || 0) : 0;
    },
    usedPercentage() {
      if (!this.summary) return 0;
      if (this.summary.UsedPercentage !== undefined && this.summary.UsedPercentage !== null) {
        return this.summary.UsedPercentage;
      }
      return this.summary.AllocationPercentage || 0;
    },
  },
  methods: {
    formatNumber,
  },
};
</script>

<style scoped>
.unassigned-card-container {
  background-color: #fffdf5;
  border: 1px solid #fde68a !important;
}
.border-amber {
  border: 1px solid #fde68a !important;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.bg-green-light {
  background-color: #f0fdf4 !important;
}
.metric-group {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}
.gap-2 {
  gap: 8px;
}
</style>
